import React, { useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import styles from "./ProfileForm.module.scss";
import { createProfile } from "../../actions/profileActions";
import { useDispatch } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import UploadPhoto from "../../components/UploadPhoto";
import { PreviewPhoto } from "../../types/siteTypes";
import { Checkbox } from "semantic-ui-react";


interface ProfileFormValues {
  displayName: string;
  bio: string;
  email: string;
  handle: string;
  country: string;
  profileImage: File | null;
  website: string;
  instagram: string;
  facebook: string;
  youtube: string;
}

const COUNTRIES = [
  { value: "Polska", labelId: "profile.country.poland" },
  { value: "Niemcy", labelId: "profile.country.germany" },
  { value: "Francja", labelId: "profile.country.france" },
  { value: "Wielka Brytania", labelId: "profile.country.unitedKingdom" },
  { value: "USA", labelId: "profile.country.usa" },
  { value: "Inne", labelId: "profile.country.other" },
];

interface ProfileFormProps {
  name?: string;
  email?: string;
  bio?: string;
  country?: string;
  website?: string;
  instagram?: string;
  facebook?: string;
  youtube?: string;
  handle?: string;
  isCreator: boolean;
}

const ProfileForm = ({ name, email, bio, country, website, instagram, facebook, youtube, handle, isCreator }: ProfileFormProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [photo, setPhoto] = useState([{ name: "", url: "" }]);
  const [becomeCreator, setBecomeCreator] = useState(false);
  const [values, setValues] = useState<ProfileFormValues>({
    displayName: name || "",
    email: email || "",
    handle: handle || "",
    bio: bio || "",
    country: country || "Polska",
    profileImage: null,
    website: website || "",
    instagram: instagram || "",
    facebook: facebook || "",
    youtube: youtube || "",
  });

  const showCreatorFields = isCreator || becomeCreator;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };



  const onAddPhoto = (pic: PreviewPhoto[]) => {
    setPhoto(pic.length > 0 ? [pic[pic.length - 1]] : []);
  };



  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(createProfile({ ...values, photo: photo[0].url, becomeCreator }, undefined, true));
  };


  return (
    <div className={styles.form}>

      {/* ── Account data ── */}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="displayName">
          <FormattedMessage
            id="manageProfile.account.displayName.label"
            defaultMessage="Enter your name or designer name"
          />
        </label>
        <input
          id="displayName"
          name="displayName"
          type="text"
          className={styles.input}
          value={values.displayName}
          onChange={handleChange}
          placeholder={intl.formatMessage({
            id: "manageProfile.account.displayName.placeholder",
            defaultMessage: "Your designer name",
          })}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="email">
          <FormattedMessage
            id="manageProfile.account.email.label"
            defaultMessage="Email address"
          />
        </label>
        <input
          id="email"
          name="email"
          className={styles.input}
          value={values.email}
          onChange={handleChange}
          placeholder={intl.formatMessage({
            id: "manageProfile.account.email.placeholder",
            defaultMessage: "you@example.com",
          })}
        />
      </div>
      {showCreatorFields && (
        <>
         


          <div className={styles.field}>
            <label className={styles.label} htmlFor="bio">
              <FormattedMessage
                id="manageProfile.account.bio.label"
                defaultMessage="Tell us about yourself"
              />
            </label>
            <textarea
              id="bio"
              name="bio"
              className={styles.textarea}
              value={values.bio}
              onChange={handleChange}
              placeholder={intl.formatMessage({
                id: "manageProfile.account.bio.placeholder",
                defaultMessage: "Describe your style and projects...",
              })}
            />
          </div>

        </>
      )}

      <div className={styles.field}>
        <label className={styles.label} htmlFor="country">
          <FormattedMessage
            id="manageProfile.account.country.label"
            defaultMessage="Country"
          />
        </label>
        <div className={styles.selectWrapper}>
          <select
            id="country"
            name="country"
            className={styles.select}
            value={values.country}
            onChange={handleChange}
          >
            {COUNTRIES.map((countryOption) => (
              <option key={countryOption.value} value={countryOption.value}>
                {intl.formatMessage({
                  id: countryOption.labelId,
                  defaultMessage: countryOption.value,
                })}
              </option>
            ))}
          </select>
          <FiChevronDown size={16} className={styles.selectArrow} />
        </div>
      </div>

      {/* ── Profile photo upload ── */}
      <div className={styles.field}>
        <p className={styles.label}>
          <FormattedMessage id="manageProfile.section.photo" defaultMessage="Profile photo" />
        </p>
        <div className={styles.photoSection}>
          {photo !== null && (
            <UploadPhoto photos={photo} updatePhotoList={onAddPhoto} />
          )}
        </div>
      </div>
   
   {!isCreator && (
     <div className={styles.checkbox}>
  <label className={styles.label} htmlFor="becomeCreator">
    <FormattedMessage
      id="manageProfile.account.becomeCreator.label"
      defaultMessage="Become a creator"
    />
  </label>

  <Checkbox
    type="checkbox"
    id="becomeCreator"
    checked={becomeCreator}
    onChange={(e: any) => setBecomeCreator(e.target.checked)}
  />
</div>
   )}

     {/* creatorSections */}
      {showCreatorFields && (
        <>
         <div className={styles.field}>
            <label className={styles.label} htmlFor="handle">
              <FormattedMessage
                id="manageProfile.account.handle.label"
                defaultMessage="Profile handle"
              />
            </label>
            <input
              id="handle"
              type="text"
              className={styles.input}
              placeholder={intl.formatMessage({
                id: "manageProfile.account.handle.placeholder",
                defaultMessage: "Unique identifier (e.g. my-profile)",
              })}
              name="handle"
              value={values.handle}
              onChange={handleChange}
            />
          </div>
          <h2 className={styles.sectionTitle}>
            <FormattedMessage
              id="manageProfile.section.social"
              defaultMessage="Social networks"
            />
          </h2>

          <div className={styles.channels}>
            {(
              [
                { name: "website", labelId: "manageProfile.social.website.label", placeholderId: "manageProfile.social.website.placeholder" },
                { name: "instagram", labelId: "manageProfile.social.instagram.label", placeholderId: "manageProfile.social.instagram.placeholder" },
                { name: "facebook", labelId: "manageProfile.social.facebook.label", placeholderId: "manageProfile.social.facebook.placeholder" },
                { name: "youtube", labelId: "manageProfile.social.youtube.label", placeholderId: "manageProfile.social.youtube.placeholder" },
              ] as const
            ).map(({ name, labelId, placeholderId }) => (
              <div className={styles.field} key={name}>
                <label className={styles.label} htmlFor={name}>
                  <FormattedMessage
                    id={labelId}
                    defaultMessage={name}
                  />
                </label>
                <input
                  id={name}
                  name={name}
                  type="text"
                  className={styles.input}
                  value={values[name]}
                  onChange={handleChange}
                  placeholder={intl.formatMessage({
                    id: placeholderId,
                    defaultMessage: "",
                  })}
                />
              </div>
            ))}
          </div>
        </>
      )}
      {/* ── Save ── */}
      <button type="button" className={styles.saveBtn} onClick={handleSubmit}>
        <FormattedMessage
          id="manageProfile.saveChanges"
          defaultMessage="Save changes"
        />
      </button>
    </div>
  );
};

export default ProfileForm;